import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { currencyUtils } from '~/utils/currency.utils';
import { fromDateStampObject, fromDatestampString, toDateStampObject } from './time';
export const MALFORMED_BILLING_TIMELINE = 'Malformed billing timeline response';
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;
const KNOWN_BILLING_TYPES = [
    'recurring',
    'oneTime'
];
const rejectMalformedResponse = (expected)=>{
    throw new Error("".concat(MALFORMED_BILLING_TIMELINE, ": expected ").concat(expected));
};
const toCalendarDate = (value)=>CALENDAR_DATE.test(value) ? toDateStampObject(fromDatestampString(value)) : rejectMalformedResponse('a date');
const toAmountInCents = (value)=>Number.isFinite(value) ? value : rejectMalformedResponse('an amount');
const toText = (value)=>typeof value === 'string' ? value : rejectMalformedResponse('text');
const toFlag = (value)=>{
    if (value == null) {
        return false;
    }
    return typeof value === 'boolean' ? value : rejectMalformedResponse('a flag');
};
const toList = (value)=>{
    if (value == null) {
        return [];
    }
    return Array.isArray(value) && value.every((element)=>element != null && typeof element === 'object') ? value : rejectMalformedResponse('a list');
};
const toBillingType = (value)=>{
    var _KNOWN_BILLING_TYPES_find;
    return (_KNOWN_BILLING_TYPES_find = KNOWN_BILLING_TYPES.find((known)=>known === value)) !== null && _KNOWN_BILLING_TYPES_find !== void 0 ? _KNOWN_BILLING_TYPES_find : 'other';
};
const formatAmountInCents = (cents)=>currencyUtils.formatDollarAmount(currencyUtils.centsToDollars(cents));
const sumAmounts = (lineItems)=>lineItems.reduce((total, lineItem)=>total + lineItem.amount(), 0);
const committedAmong = (lineItems)=>lineItems.filter((lineItem)=>!lineItem.isOptional());
const optionalAmong = (lineItems)=>lineItems.filter((lineItem)=>lineItem.isOptional());
export class ScheduledLineItemModel {
    static fromDTO(dto) {
        return new ScheduledLineItemModel(toText(dto.serviceId), toText(dto.name), toAmountInCents(dto.amount), toBillingType(dto.billingType), toFlag(dto.isFinalCharge), toFlag(dto.isProrated), toFlag(dto.isOptional));
    }
    serviceId() {
        return this.id;
    }
    name() {
        return this.serviceName;
    }
    amount() {
        return this.amountInCents;
    }
    billingType() {
        return this.type;
    }
    formattedAmount() {
        return formatAmountInCents(this.amountInCents);
    }
    isFinalCharge() {
        return this.endsTheService;
    }
    isProrated() {
        return this.coversAPartPeriod;
    }
    isOptional() {
        return this.awaitsAnOptIn;
    }
    constructor(id, serviceName, amountInCents, type, endsTheService, coversAPartPeriod, awaitsAnOptIn){
        _define_property(this, "id", void 0);
        _define_property(this, "serviceName", void 0);
        _define_property(this, "amountInCents", void 0);
        _define_property(this, "type", void 0);
        _define_property(this, "endsTheService", void 0);
        _define_property(this, "coversAPartPeriod", void 0);
        _define_property(this, "awaitsAnOptIn", void 0);
        this.id = id;
        this.serviceName = serviceName;
        this.amountInCents = amountInCents;
        this.type = type;
        this.endsTheService = endsTheService;
        this.coversAPartPeriod = coversAPartPeriod;
        this.awaitsAnOptIn = awaitsAnOptIn;
    }
}
export class ScheduledInvoiceModel {
    static fromDTO(dto) {
        return new ScheduledInvoiceModel(toCalendarDate(dto.date), toAmountInCents(dto.totalAmount), toFlag(dto.requiresApproval), toList(dto.lineItems).map(ScheduledLineItemModel.fromDTO));
    }
    date() {
        return this.invoiceDate;
    }
    key() {
        return "".concat(this.invoiceDate.year, "-").concat(this.invoiceDate.month, "-").concat(this.invoiceDate.day);
    }
    formattedDate() {
        return fromDateStampObject(this.invoiceDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    }
    formattedDateWithYear() {
        return fromDateStampObject(this.invoiceDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    }
    totalAmount() {
        return this.totalInCents;
    }
    formattedTotal() {
        return formatAmountInCents(this.totalInCents);
    }
    requiresApproval() {
        return this.needsApproval;
    }
    lineItems() {
        return this.items;
    }
    optionalAmount() {
        return sumAmounts(optionalAmong(this.items));
    }
    formattedOptionalAmount() {
        return formatAmountInCents(this.optionalAmount());
    }
    hasOptionalCharges() {
        return this.optionalAmount() !== 0;
    }
    carriesCommittedCharges() {
        return committedAmong(this.items).length > 0;
    }
    constructor(invoiceDate, totalInCents, needsApproval, items){
        _define_property(this, "invoiceDate", void 0);
        _define_property(this, "totalInCents", void 0);
        _define_property(this, "needsApproval", void 0);
        _define_property(this, "items", void 0);
        this.invoiceDate = invoiceDate;
        this.totalInCents = totalInCents;
        this.needsApproval = needsApproval;
        this.items = items;
    }
}
export class InvoiceMonthGroupModel {
    key() {
        return "".concat(this.groupYear, "-").concat(this.groupMonth);
    }
    heading() {
        return monthHeadingOf(this.groupYear, this.groupMonth);
    }
    invoices() {
        return this.monthInvoices;
    }
    invoiceCount() {
        return this.monthInvoices.length;
    }
    constructor(groupYear, groupMonth, monthInvoices){
        _define_property(this, "groupYear", void 0);
        _define_property(this, "groupMonth", void 0);
        _define_property(this, "monthInvoices", void 0);
        this.groupYear = groupYear;
        this.groupMonth = groupMonth;
        this.monthInvoices = monthInvoices;
    }
}
const WINDOW_MONTHS = 12;
const JANUARY = 1;
const HORIZON_LEAD_IN_DAYS = 8;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const shiftDays = (date, days)=>toDateStampObject(new Date(date.year, date.month - 1, date.day + days));
const shiftMonths = (date, months)=>toDateStampObject(new Date(date.year, date.month - 1 + months, date.day));
const dayCountBetween = (from, to)=>Math.round((fromDateStampObject(to).getTime() - fromDateStampObject(from).getTime()) / MILLISECONDS_PER_DAY);
const roundToTwoDecimals = (value)=>Math.round(value * 100) / 100;
const horizonStartOf = (anchor)=>shiftDays(anchor, -HORIZON_LEAD_IN_DAYS);
const horizonSpanOf = (anchor)=>dayCountBetween(horizonStartOf(anchor), shiftMonths(anchor, WINDOW_MONTHS));
const offsetPercentOf = (anchor, date)=>roundToTwoDecimals(dayCountBetween(horizonStartOf(anchor), date) / horizonSpanOf(anchor) * 100);
const monthOpeningsIn = (anchor)=>{
    const start = horizonStartOf(anchor);
    const startsOnAMonthOpening = start.day === 1;
    const firstOpening = shiftMonths({
        year: start.year,
        month: start.month,
        day: 1
    }, startsOnAMonthOpening ? 0 : 1);
    const horizonEnd = shiftMonths(anchor, WINDOW_MONTHS);
    return Array.from({
        length: WINDOW_MONTHS + 1
    }, (_, monthOffset)=>shiftMonths(firstOpening, monthOffset)).filter((opening)=>dayCountBetween(opening, horizonEnd) > 0);
};
const fallsInsideHorizon = (anchor, date)=>dayCountBetween(horizonStartOf(anchor), date) >= 0 && dayCountBetween(date, shiftMonths(anchor, WINDOW_MONTHS)) >= 0;
const lineItemsByServiceIdOf = (invoice)=>invoice.lineItems().reduce((lineItemsByServiceId, lineItem)=>{
        var _lineItemsByServiceId_get;
        return lineItemsByServiceId.set(lineItem.serviceId(), [
            ...(_lineItemsByServiceId_get = lineItemsByServiceId.get(lineItem.serviceId())) !== null && _lineItemsByServiceId_get !== void 0 ? _lineItemsByServiceId_get : [],
            lineItem
        ]);
    }, new Map());
// WHY: optionality is a property of the service's participation (committed-scope.go), constant for the schedule.
const serviceIdsOf = (lineItems)=>new Set(lineItems.map((lineItem)=>lineItem.serviceId()));
const laneDotsByServiceId = (invoices, anchor, committedServiceIds)=>invoices.filter((invoice)=>fallsInsideHorizon(anchor, invoice.date())).reduce((dotsByServiceId, invoice)=>{
        const offsetPercent = offsetPercentOf(anchor, invoice.date());
        const formattedDate = invoice.formattedDateWithYear();
        lineItemsByServiceIdOf(invoice).forEach((serviceLineItems, serviceId)=>{
            var _dotsByServiceId_get;
            const dots = (_dotsByServiceId_get = dotsByServiceId.get(serviceId)) !== null && _dotsByServiceId_get !== void 0 ? _dotsByServiceId_get : [];
            dots.push({
                key: "".concat(invoice.key(), "-").concat(serviceId),
                offsetPercent,
                formattedAmount: formatAmountInCents(sumAmounts(serviceLineItems)),
                formattedDate,
                isFinal: serviceLineItems.some((lineItem)=>lineItem.isFinalCharge()),
                isProrated: serviceLineItems.some((lineItem)=>lineItem.isProrated()),
                isOptional: !committedServiceIds.has(serviceId)
            });
            dotsByServiceId.set(serviceId, dots);
        });
        return dotsByServiceId;
    }, new Map());
const lineItemsOf = (invoices)=>invoices.flatMap((invoice)=>invoice.lineItems());
const committedLineItemsOf = (invoices)=>committedAmong(lineItemsOf(invoices));
const oneTimeLineItemsOf = (invoices)=>committedLineItemsOf(invoices).filter((lineItem)=>lineItem.billingType() === 'oneTime');
const rollUpByService = (lineItems)=>{
    const rollupsByServiceId = lineItems.reduce((rollups, lineItem)=>{
        var _ref, _ref1;
        const openRollup = rollups.get(lineItem.serviceId());
        const total = ((_ref = openRollup === null || openRollup === void 0 ? void 0 : openRollup.total) !== null && _ref !== void 0 ? _ref : 0) + lineItem.amount();
        return rollups.set(lineItem.serviceId(), {
            serviceId: lineItem.serviceId(),
            name: (_ref1 = openRollup === null || openRollup === void 0 ? void 0 : openRollup.name) !== null && _ref1 !== void 0 ? _ref1 : lineItem.name(),
            total
        });
    }, new Map());
    return [
        ...rollupsByServiceId.values()
    ].sort((left, right)=>right.total - left.total || left.name.localeCompare(right.name));
};
const optionalOnlyServicesOf = (invoices)=>{
    const nameByServiceId = optionalAmong(lineItemsOf(invoices)).reduce((names, lineItem)=>names.has(lineItem.serviceId()) ? names : names.set(lineItem.serviceId(), lineItem.name()), new Map());
    return [
        ...nameByServiceId.entries()
    ].map((param)=>{
        let [serviceId, name] = param;
        return {
            serviceId,
            name
        };
    }).sort((left, right)=>left.name.localeCompare(right.name));
};
const monthHeadingOf = (year, month)=>fromDateStampObject({
        year,
        month,
        day: 1
    }).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
    });
const shortMonthOf = (year, month)=>fromDateStampObject({
        year,
        month,
        day: 1
    }).toLocaleDateString('en-US', {
        month: 'short'
    });
const monthTickLabelOf = (year, month)=>month === JANUARY ? "".concat(shortMonthOf(year, month), " '").concat(String(year).slice(2)) : shortMonthOf(year, month);
const windowMonthsFrom = (anchor)=>{
    const anchorStartsTheMonth = anchor.day === 1;
    const monthsInWindow = anchorStartsTheMonth ? WINDOW_MONTHS : WINDOW_MONTHS + 1;
    return Array.from({
        length: monthsInWindow
    }, (_, monthOffset)=>{
        const monthsFromJanuary = anchor.month - 1 + monthOffset;
        const year = anchor.year + Math.floor(monthsFromJanuary / 12);
        const month = monthsFromJanuary % 12 + 1;
        return {
            key: "".concat(year, "-").concat(month),
            heading: monthHeadingOf(year, month),
            tickLabel: monthTickLabelOf(year, month)
        };
    });
};
const amountByServiceIdOf = (invoices)=>committedLineItemsOf(invoices).reduce((amounts, lineItem)=>{
        var _amounts_lineItem_serviceId;
        amounts[lineItem.serviceId()] = ((_amounts_lineItem_serviceId = amounts[lineItem.serviceId()]) !== null && _amounts_lineItem_serviceId !== void 0 ? _amounts_lineItem_serviceId : 0) + lineItem.amount();
        return amounts;
    }, {});
const billedInvoicesOf = (invoices)=>invoices.map((invoice)=>({
            key: invoice.key(),
            formattedDate: invoice.formattedDate(),
            formattedTotal: invoice.formattedTotal(),
            formattedOptionalAmount: invoice.hasOptionalCharges() ? invoice.formattedOptionalAmount() : null,
            lines: committedAmong(invoice.lineItems()).map((lineItem, position)=>({
                    key: "".concat(invoice.key(), "-").concat(position),
                    serviceId: lineItem.serviceId(),
                    name: lineItem.name(),
                    formattedAmount: lineItem.formattedAmount()
                })),
            optionalLines: optionalAmong(invoice.lineItems()).map((lineItem, position)=>({
                    key: "".concat(invoice.key(), "-optional-").concat(position),
                    serviceId: lineItem.serviceId(),
                    name: lineItem.name(),
                    formattedAmount: lineItem.formattedAmount()
                }))
        }));
const dropUnbilledPartMonth = (months)=>{
    var _months_at;
    const spillsIntoAPartMonth = months.length > WINDOW_MONTHS;
    const partMonthBillsNothing = ((_months_at = months.at(-1)) === null || _months_at === void 0 ? void 0 : _months_at.invoiceCount) === 0;
    return spillsIntoAPartMonth && partMonthBillsNothing ? months.slice(0, -1) : months;
};
const groupByMonth = (invoices)=>invoices.reduce((groups, invoice)=>{
        const openGroup = groups.at(-1);
        const { year, month } = invoice.date();
        const belongsToOpenGroup = (openGroup === null || openGroup === void 0 ? void 0 : openGroup.key()) === "".concat(year, "-").concat(month);
        return belongsToOpenGroup ? [
            ...groups.slice(0, -1),
            new InvoiceMonthGroupModel(year, month, [
                ...openGroup.invoices(),
                invoice
            ])
        ] : [
            ...groups,
            new InvoiceMonthGroupModel(year, month, [
                invoice
            ])
        ];
    }, []);
export class BillingTimelineModel {
    static fromDTO(dto) {
        return new BillingTimelineModel(toCalendarDate(dto.anchorDate), toList(dto.invoices).map(ScheduledInvoiceModel.fromDTO), toFlag(dto.hasManuallyBilledServices));
    }
    anchorDate() {
        return this.simulatedFrom;
    }
    invoices() {
        return this.scheduledInvoices;
    }
    hasInvoices() {
        return this.scheduledInvoices.length > 0;
    }
    hasManuallyBilledServices() {
        return this.manuallyBilledServices;
    }
    invoicesByMonth() {
        return groupByMonth(this.scheduledInvoices);
    }
    firstInvoice() {
        return this.scheduledInvoices.find((invoice)=>invoice.carriesCommittedCharges());
    }
    formattedFirstInvoiceAmount() {
        var _ref;
        var _this_firstInvoice;
        return formatAmountInCents((_ref = (_this_firstInvoice = this.firstInvoice()) === null || _this_firstInvoice === void 0 ? void 0 : _this_firstInvoice.totalAmount()) !== null && _ref !== void 0 ? _ref : 0);
    }
    monthlyTotals() {
        const invoicesByMonthKey = this.invoicesByMonth().reduce((invoices, group)=>{
            var _invoices_get;
            return invoices.set(group.key(), [
                ...(_invoices_get = invoices.get(group.key())) !== null && _invoices_get !== void 0 ? _invoices_get : [],
                ...group.invoices()
            ]);
        }, new Map());
        const plottedMonths = windowMonthsFrom(this.simulatedFrom).map((month)=>{
            var _invoicesByMonthKey_get;
            const invoices = (_invoicesByMonthKey_get = invoicesByMonthKey.get(month.key)) !== null && _invoicesByMonthKey_get !== void 0 ? _invoicesByMonthKey_get : [];
            const total = invoices.reduce((monthTotal, invoice)=>monthTotal + invoice.totalAmount(), 0);
            const optionalTotal = invoices.reduce((monthUpside, invoice)=>monthUpside + invoice.optionalAmount(), 0);
            return _object_spread_props(_object_spread({}, month), {
                total,
                optionalTotal,
                formattedOptionalTotal: optionalTotal > 0 ? formatAmountInCents(optionalTotal) : null,
                formattedTotal: formatAmountInCents(total),
                invoiceCount: invoices.length,
                billedInvoices: billedInvoicesOf(invoices),
                amountByServiceId: amountByServiceIdOf(invoices)
            });
        });
        return dropUnbilledPartMonth(plottedMonths);
    }
    annualRecurringValue() {
        return sumAmounts(committedLineItemsOf(this.scheduledInvoices).filter((lineItem)=>lineItem.billingType() === 'recurring'));
    }
    formattedAnnualRecurringValue() {
        return formatAmountInCents(this.annualRecurringValue());
    }
    oneTimeContractValue() {
        return sumAmounts(oneTimeLineItemsOf(this.scheduledInvoices));
    }
    hasOneTimeItems() {
        return oneTimeLineItemsOf(this.scheduledInvoices).length > 0;
    }
    formattedOneTimeContractValue() {
        return formatAmountInCents(this.oneTimeContractValue());
    }
    totalContractValue() {
        return sumAmounts(committedLineItemsOf(this.scheduledInvoices));
    }
    formattedTotalContractValue() {
        return formatAmountInCents(this.totalContractValue());
    }
    amountsByService() {
        return rollUpByService(committedLineItemsOf(this.scheduledInvoices));
    }
    serviceLanes() {
        const committedLineItems = committedLineItemsOf(this.scheduledInvoices);
        const committedServiceIds = serviceIdsOf(committedLineItems);
        const dotsByServiceId = laneDotsByServiceId(this.scheduledInvoices, this.simulatedFrom, committedServiceIds);
        return [
            ...rollUpByService(committedLineItems),
            ...optionalOnlyServicesOf(this.scheduledInvoices)
        ].map((service)=>{
            var _dotsByServiceId_get;
            return {
                serviceId: service.serviceId,
                name: service.name,
                dots: (_dotsByServiceId_get = dotsByServiceId.get(service.serviceId)) !== null && _dotsByServiceId_get !== void 0 ? _dotsByServiceId_get : []
            };
        });
    }
    horizonTicks() {
        return monthOpeningsIn(this.simulatedFrom).map((opening)=>({
                key: "".concat(opening.year, "-").concat(opening.month),
                label: monthTickLabelOf(opening.year, opening.month),
                offsetPercent: offsetPercentOf(this.simulatedFrom, opening)
            }));
    }
    anchorOffsetPercent() {
        return offsetPercentOf(this.simulatedFrom, this.simulatedFrom);
    }
    constructor(simulatedFrom, scheduledInvoices, manuallyBilledServices){
        _define_property(this, "simulatedFrom", void 0);
        _define_property(this, "scheduledInvoices", void 0);
        _define_property(this, "manuallyBilledServices", void 0);
        this.simulatedFrom = simulatedFrom;
        this.scheduledInvoices = scheduledInvoices;
        this.manuallyBilledServices = manuallyBilledServices;
    }
}
